import { formatCurrency, formatDay, formatMonth, fullName } from './format';
import { balanceDue, getPaymentState, medicalReviewLabel } from './registrations';

/*
 * Column metadata: label, sort type, sort value and filter values per column. Rendering lives in
 * components/table/columns.
 *
 * Program questions are custom per program, so they get numbered pairs in the order stored on the
 * booking, as the master export has them (Question 1 / Answer 1 ...): Question N on one row can be
 * a different question from Question N on the next.
 */

export const BLANK = '(Blank)';

const orBlank = value => (String(value ?? '').trim() ? String(value) : BLANK);

// T-shirt / shorts sizes arrive as free text ("Youth Medium", "Adult XX-Large", "AL", "YXS").
// Youth before adult, then XXS to XXXL; anything unrecognised sorts after them.
const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
export function sizeRank(value) {
  const text = String(value || '').trim().toUpperCase();
  if (!text) return 999;
  let group = 1;
  let size = text;
  const word = text.match(/^(YOUTH|ADULT|Y|A)\s*(.*)$/);
  if (word) {
    group = word[1].startsWith('Y') ? 0 : 1;
    size = word[2];
  }
  size = size.replace(/[\s-]+/g, '').replace(/EXTRA/g, 'X');
  const spelled = size.match(/^(X*)(SMALL|MEDIUM|LARGE)$/);
  if (spelled) size = spelled[1] + spelled[2][0];
  const index = SIZE_ORDER.indexOf(size);
  return index < 0 ? 500 : group * 100 + index;
}

const MEDICAL_REVIEW_RANK = { 'Needs review': 0, 'No document': 1, Approved: 2, 'Not required': 3 };

const money = r => r.money || { participantFees: {}, booking: {} };
const extraTitles = r => r.extras.map(e => e.title).filter(Boolean);

export const PINNED_KEYS = ['bookingDate', 'participant'];

export const baseColumnOrder = [
  'bookingDate', 'participant', 'bookingNumber', 'program', 'status', 'statusNote', 'stay', 'age', 'grade',
  'dob', 'gender', 'primaryGuardian', 'secondaryGuardian', 'address', 'apt', 'city', 'state', 'zip',
  'extras', 'additionalCharge', 'tshirtSize', 'shortsSize', 'payment', 'paymentType', 'couponCode',
  'couponAmount', 'insuranceCompany', 'documents', 'medicalReview', 'medical', 'allergy', 'groupCode',
];

export const baseColumnMeta = {
  bookingDate: { label: 'Booking date', sortType: 'date', sortValue: r => r.bookingDate || 0, filterValues: r => [orBlank(formatMonth(r.bookingDate))] },
  participant: { label: 'Participant', sortType: 'text', sortValue: r => fullName(r), filterValues: r => [orBlank(fullName(r))] },
  bookingNumber: { label: 'Booking #', sortType: 'text', sortValue: r => r.bookingNumber, filterValues: r => [orBlank(r.bookingNumber)] },
  program: { label: 'Program', sortType: 'text', sortValue: r => r.program.programName, filterValues: r => [orBlank(r.program.programName)] },
  status: { label: 'Status', sortType: 'text', sortValue: r => r.status, filterValues: r => [r.status] },
  statusNote: { label: 'Status note', sortType: 'text', sortValue: r => r.statusNote, filterValues: r => [orBlank(r.statusNote)] },
  stay: { label: 'Commuter / Overnight', sortType: 'text', sortValue: r => r.stay, filterValues: r => [orBlank(r.stay)] },
  age: { label: 'Age', sortType: 'number', sortValue: r => (r.participant.age ?? ''), filterValues: r => [orBlank(r.participant.age)] },
  grade: { label: 'Grade', sortType: 'text', sortValue: r => r.participant.grade, filterValues: r => [orBlank(r.participant.grade)] },
  dob: { label: 'Date of birth', sortType: 'date', sortValue: r => r.participant.dob ?? '', filterValues: r => [orBlank(formatDay(r.participant.dob))] },
  gender: { label: 'Gender', sortType: 'text', sortValue: r => r.participant.gender, filterValues: r => [orBlank(r.participant.gender)] },
  primaryGuardian: { label: 'Primary guardian', sortType: 'text', sortValue: r => r.primaryGuardian.name, filterValues: r => [orBlank(r.primaryGuardian.name)] },
  secondaryGuardian: { label: 'Secondary guardian', sortType: 'text', sortValue: r => r.secondaryGuardian?.name ?? '', filterValues: r => [orBlank(r.secondaryGuardian?.name)] },
  address: { label: 'Address', sortType: 'text', sortValue: r => r.address.street, filterValues: r => [orBlank(r.address.street)] },
  apt: { label: 'Apartment #', sortType: 'text', sortValue: r => r.address.apt, filterValues: r => [orBlank(r.address.apt)] },
  city: { label: 'City', sortType: 'text', sortValue: r => r.address.city, filterValues: r => [orBlank(r.address.city)] },
  state: { label: 'State', sortType: 'text', sortValue: r => r.address.state, filterValues: r => [orBlank(r.address.state)] },
  zip: { label: 'Zip code', sortType: 'text', sortValue: r => r.address.zip, filterValues: r => [orBlank(r.address.zip)] },
  // A row can list the same add-on twice; each value counts once per row.
  extras: { label: 'Additional experience', sortType: 'text', sortValue: r => extraTitles(r).join(', '), filterValues: r => (extraTitles(r).length ? [...new Set(extraTitles(r))] : [BLANK]) },
  additionalCharge: {
    label: 'Additional charge', sortType: 'number',
    sortValue: r => Number(money(r).participantFees.additionalCharges) || 0,
    filterValues: r => [Number(money(r).participantFees.additionalCharges) ? formatCurrency(money(r).participantFees.additionalCharges) : BLANK],
  },
  tshirtSize: { label: 'T-shirt size', sortType: 'number', sortValue: r => sizeRank(r.participant.tshirtSize), filterValues: r => [orBlank(r.participant.tshirtSize)] },
  shortsSize: { label: 'Shorts size', sortType: 'number', sortValue: r => sizeRank(r.participant.shortsSize), filterValues: r => [orBlank(r.participant.shortsSize)] },
  payment: { label: 'Payment', sortType: 'number', sortValue: r => balanceDue(r), filterValues: r => [orBlank(getPaymentState(r))] },
  paymentType: { label: 'Payment type', sortType: 'text', sortValue: r => money(r).booking.paymentMethod || '', filterValues: r => [orBlank(money(r).booking.paymentMethod)] },
  couponCode: { label: 'Coupon code', sortType: 'text', sortValue: r => money(r).booking.couponCode || '', filterValues: r => [orBlank(money(r).booking.couponCode)] },
  // The participant's Coupon line on the booking page (Charges card), not the booking's couponValue,
  // which every row of a booking shares; the summary card adds that once per booking.
  couponAmount: {
    label: 'Coupon amount', sortType: 'number',
    sortValue: r => Number(money(r).participantFees.couponDiscount) || 0,
    filterValues: r => [Number(money(r).participantFees.couponDiscount) ? formatCurrency(money(r).participantFees.couponDiscount) : BLANK],
  },
  insuranceCompany: { label: 'Insurance company', sortType: 'text', sortValue: r => r.participant.insuranceCompany, filterValues: r => [r.participant.insuranceCompany || 'Not on file'] },
  documents: {
    label: 'Documents', sortType: 'number',
    // By documents still missing (ascending: most missing first), so a commuter, who has three to
    // send, compares fairly with a participant who has four.
    sortValue: r => -(r.documents?.missingCount || 0),
    filterValues: r => [(r.documents?.missingCount || 0) === 0 ? 'Complete' : 'Missing documents'],
  },
  medicalReview: { label: 'Medical review', sortType: 'number', sortValue: r => MEDICAL_REVIEW_RANK[medicalReviewLabel(r)], filterValues: r => [medicalReviewLabel(r)] },
  medical: { label: 'Medical', sortType: 'text', sortValue: r => r.participant.medical, filterValues: r => [orBlank(r.participant.medical)] },
  allergy: { label: 'Allergy', sortType: 'text', sortValue: r => r.participant.allergy, filterValues: r => [orBlank(r.participant.allergy)] },
  groupCode: { label: 'Group code', sortType: 'text', sortValue: r => r.participant.groupCode, filterValues: r => [orBlank(r.participant.groupCode)] },
};

// Wording of a stored answer that has none: the question has since been removed from the program.
export const REMOVED_QUESTION = 'Removed question';

export const questionKey = n => `question${n}`;
export const answerKey = n => `answer${n}`;

// 'question7' → { kind: 'question', n: 7 }; null for any other key.
export function parseAnswerPairKey(key) {
  const match = String(key).match(/^(question|answer)([1-9]\d*)$/);
  return match ? { kind: match[1], n: Number(match[2]) } : null;
}

// The row's Nth answer (1-based), in stored order.
export const nthAnswer = (r, n) => r.answers?.[n - 1] || null;

export const questionText = (r, n) => {
  const a = nthAnswer(r, n);
  if (!a) return '';
  return String(a.question || '').trim() || REMOVED_QUESTION;
};

export const answerText = (r, n) => String(nthAnswer(r, n)?.answer ?? '');

// As many pairs as the row with the most answers.
export const answerPairCount = rows => rows.reduce((max, r) => Math.max(max, r.answers?.length || 0), 0);

function answerPairColumns(pairCount) {
  const order = [];
  const meta = {};
  for (let n = 1; n <= pairCount; n += 1) {
    order.push(questionKey(n), answerKey(n));
    meta[questionKey(n)] = { label: `Question ${n}`, sortType: 'text', sortValue: r => questionText(r, n), filterValues: r => [orBlank(questionText(r, n))] };
    meta[answerKey(n)] = { label: `Answer ${n}`, sortType: 'text', sortValue: r => answerText(r, n), filterValues: r => [orBlank(answerText(r, n))] };
  }
  return { order, meta };
}

// The fixed columns, then `pairCount` Question / Answer pairs.
export function buildColumns(pairCount = 0) {
  const pairs = answerPairColumns(pairCount);
  return { order: [...baseColumnOrder, ...pairs.order], meta: { ...baseColumnMeta, ...pairs.meta } };
}

function compareValues(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function sortRows(rows, sort, meta) {
  const column = meta[sort.key];
  if (!column) return rows;
  const dir = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = column.sortValue(a);
    const bv = column.sortValue(b);
    // Blanks always sort last.
    if (av === '' && bv !== '') return 1;
    if (bv === '' && av !== '') return -1;
    return compareValues(av, bv) * dir;
  });
}

export function matchesColumnFilters(r, filters, textFilters, meta) {
  for (const key of Object.keys(filters)) {
    const selected = filters[key];
    if (!meta[key]) continue;
    if (selected && selected.length && !meta[key].filterValues(r).some(v => selected.includes(v))) return false;
  }
  for (const key of Object.keys(textFilters)) {
    const q = textFilters[key]?.trim().toLowerCase();
    if (!meta[key]) continue;
    if (q && !meta[key].filterValues(r).some(v => v !== BLANK && v.toLowerCase().includes(q))) return false;
  }
  return true;
}

export function buildFilterOptions(rows, key, meta) {
  const column = meta[key];
  const map = new Map();
  rows.forEach(r => {
    column.filterValues(r).forEach(value => {
      const existing = map.get(value);
      if (existing) existing.count += 1;
      else map.set(value, { count: 1, sort: column.sortValue(r) });
    });
  });
  return [...map.entries()]
    .sort(([av, a], [bv, b]) => {
      if (av === BLANK) return 1;
      if (bv === BLANK) return -1;
      if (column.sortType === 'text') return compareValues(av, bv);
      return compareValues(a.sort, b.sort);
    })
    .map(([value, { count }]) => ({ value, count }));
}

export function sortLabels(type) {
  if (type === 'date') return { asc: 'Oldest first', desc: 'Newest first' };
  if (type === 'number') return { asc: 'Low to high', desc: 'High to low' };
  return { asc: 'Sort A → Z', desc: 'Sort Z → A' };
}
